import {Component, Input} from '@angular/core';
import {CommonModule} from '@angular/common';
import {DailyCheckinsV2} from '@shared/api/models/query/patient-daily-snapshot.model';

/**
 * Daily check-in card — v2 contract (UNIVAPLAT-2378, Task UNIVAPLAT-2360).
 *
 * Consumes the `dailyCheckins` object of the v2 daily statistics payload
 * (UNIVAPLAT-2359 contract, comment 27625) and renders exactly three rows:
 * Stress (0-100 score + word label), Mood (core emotion chip + optional
 * finer word) and Sleep (felt-state chip + yellow sleep-factor pills),
 * plus a patient-only Contributing-factors row.
 *
 * Variants:
 * - 'patient' (default) — renders everything, including the patient-only
 *   sleep-factor pills and contributing-factor chips.
 * - 'carer'  — NEVER renders sleep-factor pills or contributing-factor
 *   chips, even if the payload were to carry them.
 *
 * Legacy-era handling (old SQU / YEM mapped days):
 * - sleep.feltState === null with factors present → factor pills only,
 *   no felt-state chip.
 * - mood.fine missing/null → core-only chip.
 *
 * Colour classes reuse the existing severity colour rules
 * (green / amber / red / grey / yellow) — same visual language as the
 * timeline-card behaviour pills; no new palette is introduced.
 */
@Component({
    selector: 'cdss-daily-checkin-card',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './daily-checkin-card.component.html',
    styleUrl: './daily-checkin-card.component.scss'
})
export class DailyCheckinCardComponent {

    @Input() checkins: DailyCheckinsV2 | null = null;
    @Input() variant: 'patient' | 'carer' = 'patient';

    get isPatient(): boolean {
        return this.variant === 'patient';
    }

    /**
     * Backend-authoritative "was this section answered" flags (UNIVAPLAT-2360 follow-up).
     * These used to be inferred client-side from whether the displayed value happened to be
     * non-null, which broke in two ways: a legacy SQU sleep check completed with zero factors
     * selected (feltState is always null in the legacy era, so there was nothing non-null left
     * to key off), and a UDC question answered via its explicit "skip" escape hatch (per the
     * approved Confluence decision, a skipped section counts as completed — yet its value is
     * still null). The backend now computes `answered` once, with the same
     * CheckinCompletionResolver the weekly Timeline uses, so Daily Summary and Timeline can
     * never silently disagree again — the frontend just renders it. */
    get stressAnswered(): boolean {
        return this.checkins?.stress?.answered ?? false;
    }

    get moodAnswered(): boolean {
        return this.checkins?.mood?.answered ?? false;
    }

    get sleepAnswered(): boolean {
        return this.checkins?.sleep?.answered ?? false;
    }

    /**
     * Patient-only yellow sleep-factor pills, sourced from `sleep.factors`
     * (single unified field — UNIVAPLAT-2359 fix-up removed the duplicated
     * top-level `sleepFactors` member from the contract).
     * The carer variant returns [] regardless of payload content.
     */
    get sleepFactorPills(): string[] {
        if (!this.isPatient || !this.checkins) return [];
        return this.checkins.sleep?.factors ?? [];
    }

    /** Patient-only contributing-factor chips; [] on the carer variant. */
    get contributingFactorChips(): string[] {
        if (!this.isPatient || !this.checkins) return [];
        return this.checkins.contributingFactors ?? [];
    }

    /**
     * Whether the Contributing factors row counts as answered. Contributing factors are
     * collected as part of the same stress check (see
     * PatientDailyStatisticsSnapshotService::buildDailyCheckins — `contributingFactors` is
     * sourced from the UDC `stress`/legacy SLE `stress_driving_reasons` answer, not mood/emotion)
     * rather than a standalone question, so there is no separate backend `answered` flag for it —
     * it mirrors `stressAnswered`: an empty list on an answered stress check means "none
     * selected" (a real answer), while an empty list on an unanswered stress check means the row
     * itself was never reached that day.
     */
    get contributingFactorsAnswered(): boolean {
        return this.stressAnswered;
    }

    /**
     * Stress word-label chip colour, from the contract's six 0-100 bands
     * (barely there / gentle / noticeable → green-amber, pressing / heavy /
     * overwhelming → amber-red), collapsed onto the existing severity
     * classes: ≤33 green, 34-67 amber, ≥68 red.
     */
    get stressBandClass(): string {
        const value = this.checkins?.stress?.value;
        if (value === null || value === undefined) return 'grey';
        if (value <= 33) return 'green';
        if (value <= 67) return 'amber';
        return 'red';
    }

    /**
     * Mood chip colour — core-emotion valence mapped onto the existing
     * severity classes. Matching is case-insensitive; the label itself is
     * rendered verbatim as delivered by the API.
     */
    get moodColourClass(): string {
        const core = this.checkins?.mood?.core?.toLowerCase();
        switch (core) {
            case 'happy':
            case 'calm':
                return 'green';
            case 'surprised':
            case 'sad':
            case 'bad':
            case 'fearful':
                return 'amber';
            case 'angry':
            case 'disgusted':
                return 'red';
            default:
                return 'grey';
        }
    }

    /**
     * Sleep felt-state chip colour — the five felt states (Energised /
     * Refreshed / Okay / Tired / Exhausted) mapped onto the existing
     * severity classes. Case-insensitive; label rendered verbatim.
     */
    get sleepColourClass(): string {
        const state = this.checkins?.sleep?.feltState?.toLowerCase();
        switch (state) {
            case 'energised':
            case 'refreshed':
                return 'green';
            case 'okay':
            case 'tired':
                return 'amber';
            case 'exhausted':
                return 'red';
            default:
                return 'grey';
        }
    }

    trackByIndex(index: number): number {
        return index;
    }
}
